import { useState } from "react";

/**
 * Postal-code lookup + manual entry.
 *
 * • UK: postcodes.io (rich UK data, no key)
 * • Non-UK supported: zippopotam.us (worldwide postcode -> city/region, no key)
 * • Anything else: component hides itself — buyer types manually
 *
 * Both providers are HTTPS, free, no signup required.
 */

// Zippopotam.us supported countries (subset we care about for the RAX audience).
// Reference: https://api.zippopotam.us
const ZIPPO_COUNTRIES = new Set([
    "US", "CA", "AU", "NZ",
    "DE", "FR", "IT", "ES", "NL", "BE", "AT", "PT", "CH", "SE", "DK", "FI", "NO", "IE", "LU", "GR",
    "JP", "MX", "PL", "IN", "BR",
]);

const COUNTRY_LABEL = {
    GB: "UK", US: "US", CA: "Canadian", AU: "Australian", NZ: "NZ",
    DE: "German", FR: "French", IT: "Italian", ES: "Spanish", NL: "Dutch",
    BE: "Belgian", AT: "Austrian", PT: "Portuguese", CH: "Swiss", SE: "Swedish",
    DK: "Danish", FI: "Finnish", NO: "Norwegian", IE: "Irish", LU: "Luxembourg",
    GR: "Greek", JP: "Japanese", MX: "Mexican", PL: "Polish", IN: "Indian", BR: "Brazilian",
};

export default function AddressLookup({ country, onFill, onManual }) {
    const [pc, setPc] = useState("");
    const [looking, setLooking] = useState(false);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState(false);

    const isUK = country === "GB";
    const isSupported = isUK || ZIPPO_COUNTRIES.has(country);
    if (!isSupported) return null;

    const label = COUNTRY_LABEL[country] || country;

    const lookup = async () => {
        const trimmed = pc.trim().toUpperCase().replace(/\s+/g, "");
        if (!trimmed) { setErr(`Please enter a ${label} postcode.`); return; }
        setErr(""); setOk(false); setLooking(true);
        try {
            if (isUK) {
                const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(trimmed)}`);
                if (!r.ok) {
                    setErr("We couldn't find that postcode. Please check it or enter your address manually below.");
                    return;
                }
                const { result } = await r.json();
                onFill({
                    city: result.post_town || result.admin_district || "",
                    postal_code: result.postcode || trimmed,
                    country: "GB",
                });
            } else {
                // zippopotam.us — supports many countries with the same shape
                const r = await fetch(`https://api.zippopotam.us/${country.toLowerCase()}/${encodeURIComponent(trimmed)}`);
                if (!r.ok) {
                    setErr("We couldn't find that postcode. Please check it or enter your address manually below.");
                    return;
                }
                const data = await r.json();
                const place = (data.places || [])[0] || {};
                onFill({
                    // Zippopotam returns { "place name": "London", "state": "England", "post code": "..." }
                    city: place["place name"] || "",
                    postal_code: data["post code"] || trimmed,
                    country,
                });
            }
            setOk(true);
        } catch {
            setErr("Postcode lookup is unavailable right now. Please enter your address manually below.");
        } finally {
            setLooking(false);
        }
    };

    return (
        <div
            data-testid="address-lookup"
            style={{
                gridColumn: "1 / -1",
                background: "var(--parchment-soft)",
                border: "1px solid var(--sand-line)",
                padding: 16,
                marginBottom: 4,
            }}
        >
            <div style={{
                fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em",
                textTransform: "uppercase", color: "var(--cocoa)", marginBottom: 8,
            }}>
                Find your {label} address
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
                <input
                    className="input"
                    style={{ flex: "1 1 200px", textTransform: "uppercase" }}
                    placeholder={
                        isUK ? "Enter postcode (e.g. SW1A 1AA)"
                             : country === "US" ? "Enter ZIP code (e.g. 10001)"
                             : "Enter postcode"
                    }
                    value={pc}
                    onChange={(e) => setPc(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }}
                    data-testid="address-lookup-input"
                    aria-label={`${label} postcode`}
                />
                <button
                    type="button"
                    className="btn btn-outline"
                    onClick={lookup}
                    disabled={looking}
                    data-testid="address-lookup-btn"
                    style={{ whiteSpace: "nowrap" }}
                >
                    {looking ? "Searching…" : "Find address"}
                </button>
            </div>
            {err && (
                <div className="form-error" style={{ marginTop: 10 }} data-testid="address-lookup-error">
                    {err}
                    <button
                        type="button"
                        onClick={onManual}
                        style={{
                            marginLeft: 8, background: "transparent", border: "none",
                            textDecoration: "underline", color: "var(--cocoa)", cursor: "pointer",
                            fontFamily: "inherit", fontSize: "inherit",
                        }}
                        data-testid="address-lookup-manual"
                    >
                        Enter manually
                    </button>
                </div>
            )}
            {ok && (
                <div data-testid="address-lookup-success" style={{
                    marginTop: 10, fontFamily: "var(--font-serif)", fontStyle: "italic",
                    color: "var(--cocoa)", fontSize: 14,
                }}>
                    ✓ Found. Please add your house number and street in the address line below.
                </div>
            )}
            <div style={{
                marginTop: 10, fontFamily: "var(--font-serif)", color: "var(--cocoa)",
                opacity: 0.7, fontSize: 12,
            }}>
                Prefer to type it out?{" "}
                <button
                    type="button"
                    onClick={onManual}
                    style={{
                        background: "transparent", border: "none", padding: 0,
                        textDecoration: "underline", color: "var(--cocoa)", cursor: "pointer",
                        fontFamily: "inherit", fontSize: "inherit",
                    }}
                >
                    Enter address manually
                </button>
                {" "}below.
            </div>
        </div>
    );
}
